// Mix-and-match closet: a one-at-a-time bag track and a scarf strip. Whichever item sits in the
// centre of each track is the current pick; the scarf pick is layered onto the bag stage and the
// look summary, total and "Add the look" form follow both picks.
class SCloset extends HTMLElement {
  #ac;
  #bagTrack;
  #scarfTrack;
  #bag = null;
  #scarf = null;
  #timers = new Map();
  #formatter;

  connectedCallback() {
    this.#ac = new AbortController();
    const signal = this.#ac.signal;
    this.#bagTrack = this.querySelector('[data-closet-bags]');
    this.#scarfTrack = this.querySelector('[data-closet-scarves]');
    if (!this.#bagTrack || !this.#scarfTrack) return;

    try {
      this.#formatter = new Intl.NumberFormat(this.dataset.locale || undefined, {
        style: 'currency',
        currency: this.dataset.currency || 'GBP',
      });
    } catch {
      this.#formatter = null;
    }

    for (const track of [this.#bagTrack, this.#scarfTrack]) {
      track.addEventListener('scroll', () => this.#queueSync(track), { passive: true, signal });
    }
    window.addEventListener('resize', () => {
      this.#sync();
      this.#place();
    }, { signal });

    for (const img of this.querySelectorAll('img[data-closet-cutout]')) {
      // Process every bag up front from its own URL, so off-screen (lazy) bags are centred too.
      this.#queueCutout(img);
    }

    this.addEventListener('command', (event) => {
      const map = {
        '--closet-prev-bag': [this.#bagTrack, -1],
        '--closet-next-bag': [this.#bagTrack, 1],
        '--closet-prev-scarf': [this.#scarfTrack, -1],
        '--closet-next-scarf': [this.#scarfTrack, 1],
      };
      if (event.command === '--closet-prev-colour') return this.#stepColour(-1);
      if (event.command === '--closet-next-colour') return this.#stepColour(1);
      const entry = map[event.command];
      if (!entry) return;
      this.#step(entry[0], entry[1]);
    }, { signal });

    this.#scarfTrack.addEventListener('click', (event) => {
      const button = event.target.closest('[data-closet-item]');
      if (!button || !this.#scarfTrack.contains(button)) return;
      this.#center(this.#scarfTrack, button.closest('li'));
      this.#select('scarf', button);
    }, { signal });

    this.querySelector('form[data-closet-form]')?.addEventListener('submit', (event) => {
      if (!window.Shopify?.actions?.updateCart) return;
      event.preventDefault();
      this.#addLook(event.submitter);
    }, { signal });

    // A ?bag=3 (or #bag-3) link opens the closet on that bag.
    const linked = window.location.hash.match(/^#bag-(\d+)$/) || [null, new URLSearchParams(window.location.search).get('bag')];
    if (linked?.[1]) this.#bagTrack.scrollLeft = (Number(linked[1]) - 1) * this.#bagTrack.clientWidth;

    this.#sync();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    for (const timer of this.#timers.values()) clearTimeout(timer);
  }

  #items(track) {
    return [...track.querySelectorAll('[data-closet-item]')];
  }

  #queueSync(track) {
    clearTimeout(this.#timers.get(track));
    this.#timers.set(track, setTimeout(() => this.#syncTrack(track), 90));
  }

  #sync() {
    this.#syncTrack(this.#bagTrack);
    this.#syncTrack(this.#scarfTrack);
  }

  #syncTrack(track) {
    const items = this.#items(track);
    if (!items.length) return;
    if (!this.#isVisible(track)) {
      const current = track === this.#bagTrack ? this.#bag : this.#scarf;
      if (!current) this.#select(track === this.#bagTrack ? 'bag' : 'scarf', items[0]);
      return;
    }
    const box = track.getBoundingClientRect();
    const middle = box.left + box.width / 2;
    let best = items[0];
    let bestDistance = Infinity;
    for (const item of items) {
      const rect = item.closest('li').getBoundingClientRect();
      const distance = Math.abs(rect.left + rect.width / 2 - middle);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = item;
      }
    }
    this.#select(track === this.#bagTrack ? 'bag' : 'scarf', best);
  }

  #step(track, direction) {
    const items = this.#items(track);
    const current = track === this.#bagTrack ? this.#bag : this.#scarf;
    const index = Math.max(0, items.indexOf(current));
    const rtl = getComputedStyle(track).direction === 'rtl' ? -1 : 1;
    const next = items[(index + direction * rtl + items.length) % items.length];
    if (!next) return;
    if (track === this.#bagTrack && this.#isVisible(track)) this.#fadeToBag(next);
    else if (this.#isVisible(track)) this.#center(track, next.closest('li'));
    else this.#select(track === this.#bagTrack ? 'bag' : 'scarf', next);
  }

  // The arrow changes the bag the same way the scarf changes: the look fades out, the next bag
  // is put in place, then everything fades back in.
  async #fadeToBag(next) {
    const track = this.#bagTrack;
    const overlay = this.querySelector('[data-closet-overlay]');
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const token = (this.#bagFade = (this.#bagFade || 0) + 1);
    if (!reduce) {
      track.style.transition = 'opacity 200ms ease';
      track.style.opacity = '0';
      overlay?.classList.add('opacity-0');
      await new Promise((resolve) => setTimeout(resolve, 200));
      if (token !== this.#bagFade) return;
    }
    this.#center(track, next.closest('li'), true);
    this.#select('bag', next);
    track.style.opacity = '';
    if (overlay?.naturalWidth) overlay.classList.remove('opacity-0');
  }

  #bagFade = 0;

  #colours(item) {
    return item ? [...item.querySelectorAll('[data-closet-colour]')] : [];
  }

  // Up and down step through the current bag's colours (looping): the bag fades out, its photo,
  // price and cart item switch to the next colour, then it fades back in.
  async #stepColour(direction) {
    const item = this.#bag;
    const colours = this.#colours(item);
    if (colours.length < 2) return;
    const index = Math.max(0, colours.findIndex((c) => c.dataset.variantId === item.dataset.variantId));
    const colour = colours[(index + direction + colours.length) % colours.length];
    const img = item.querySelector('img');
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const token = (this.#colourFade = (this.#colourFade || 0) + 1);

    Object.assign(item.dataset, {
      variantId: colour.dataset.variantId,
      price: colour.dataset.price,
      label: colour.dataset.label,
      colour: colour.dataset.colour,
      url: colour.dataset.url,
    });
    this.#updateSummary();
    this.#showColourName(item);
    const status = this.querySelector('[data-closet-colour-status]');
    if (status) {
      status.textContent = (status.dataset.template || '')
        .replace('__BAG__', item.dataset.title || '')
        .replace('__COLOUR__', colour.dataset.colour || '');
    }

    if (!img || !colour.dataset.image) return;
    const nextSrc = colour.dataset.cutoutSrc || colour.dataset.image;
    if (img.dataset.cutoutSrc === nextSrc) return;
    if (!reduce) {
      img.style.transition = 'opacity 180ms ease';
      img.style.opacity = '0';
    }
    const [cut] = await Promise.all([
      this.#cutoutUrl(nextSrc),
      new Promise((resolve) => setTimeout(resolve, reduce ? 0 : 180)),
    ]);
    if (token !== this.#colourFade) return;
    img.removeAttribute('srcset');
    img.removeAttribute('sizes');
    img.dataset.cutoutSrc = nextSrc;
    img.src = cut || colour.dataset.image;
    img.classList.toggle('mix-blend-multiply', !cut);
    try { await img.decode(); } catch { /* keep the last known size */ }
    if (token !== this.#colourFade) return;
    img.dataset.cutoutDone = cut ? 'true' : 'false';
    this.#place();
    img.style.opacity = '';
  }

  #colourFade = 0;

  #showColourName(item) {
    const name = this.querySelector('[data-closet-colour-name]');
    if (name && item) name.textContent = item.dataset.colour || '';
  }

  #isVisible(track) {
    return !track.hidden && track.offsetWidth > 0;
  }

  #center(track, li, instant = false) {
    if (!li) return;
    const reduce = instant || window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const trackBox = track.getBoundingClientRect();
    const box = li.getBoundingClientRect();
    const offset = box.left + box.width / 2 - (trackBox.left + trackBox.width / 2);
    track.scrollBy({ left: offset, behavior: reduce ? 'auto' : 'smooth' });
  }

  #select(kind, item) {
    if (kind === 'bag') {
      if (this.#bag === item) return;
      this.#bag = item;
      const items = this.#items(this.#bagTrack);
      items.forEach((el) => el.closest('li').setAttribute('aria-current', String(el === item)));
      const position = this.querySelector('[data-closet-position]');
      if (position) position.textContent = `${items.indexOf(item) + 1} / ${items.length}`;
      this.#updateArrows(this.#bagTrack, items, item, 'bag');
      const colourControls = this.querySelector('[data-closet-colours]');
      if (colourControls) colourControls.hidden = this.#colours(item).length < 2;
      this.#showColourName(item);
      this.#place();
    } else {
      if (this.#scarf === item) return;
      this.#scarf = item;
      const items = this.#items(this.#scarfTrack);
      items.forEach((el) => el.setAttribute('aria-pressed', String(el === item)));
      const scarfPosition = this.querySelector('[data-closet-scarf-position]');
      if (scarfPosition) scarfPosition.textContent = `${items.indexOf(item) + 1} / ${items.length}`;
      this.#updateArrows(this.#scarfTrack, items, item, 'scarf');
      this.#updateOverlay(item);
    }
    this.#updateSummary();
  }

  #updateArrows(track, items, item, name) {
    // The arrows loop through the options, so they never disable.
    void track; void items; void item; void name;
  }

  async #updateOverlay(item) {
    const overlay = this.querySelector('[data-closet-overlay]');
    if (!overlay || !item.dataset.image) return;
    overlay.classList.add('opacity-0');
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const [cut] = await Promise.all([
      this.#cutoutUrl(item.dataset.image, true),
      new Promise((resolve) => setTimeout(resolve, reduce ? 0 : 180)),
    ]);
    if (this.#scarf !== item) return;
    overlay.removeAttribute('srcset');
    overlay.src = cut || item.dataset.image;
    overlay.classList.toggle('mix-blend-multiply', !cut);
    overlay.alt = item.dataset.label || '';
    try { await overlay.decode(); } catch { /* place with the last known size */ }
    if (this.#scarf !== item) return;
    this.#place();
    overlay.classList.remove('opacity-0');
  }

  // Draws the bag and scarf to one real-world scale: the stage spans 55cm, the bag box is
  // sized in Liquid from the bag's width, and the twilly hangs folded in half from the
  // handle, so its photo (tails filling ~88% of the frame) shows half its length.
  #place() {
    const stageWidth = this.#bagTrack.clientWidth;
    if (!stageWidth) return;
    const cmToPx = stageWidth / 55;
    const stageHeight = this.#bagTrack.clientHeight || stageWidth;
    const length = Number(this.#scarf?.dataset.lengthCm) || 90;
    // Once cut out and cropped, the twilly photo shows it folded in half, so its height is half its length.
    const hang = (length / 2) * cmToPx;

    // Size every bag from its real width; the stage centres each one.
    let current = null;
    for (const item of this.#items(this.#bagTrack)) {
      const img = item.querySelector('img');
      let boxWidth;
      let boxHeight;
      if (img?.dataset.cutoutDone === 'true' && img.naturalWidth) {
        boxWidth = (Number(item.dataset.widthCm) || 28) * cmToPx;
        boxHeight = boxWidth * (img.naturalHeight / img.naturalWidth);
        const maxHeight = Math.min(stageWidth * 0.92, stageHeight * 0.88);
        if (boxHeight > maxHeight) {
          boxWidth *= maxHeight / boxHeight;
          boxHeight = maxHeight;
        }
        // Keep the widest bags clear of the side arrows.
        const maxWidth = stageWidth * 0.8;
        if (boxWidth > maxWidth) {
          boxHeight *= maxWidth / boxWidth;
          boxWidth = maxWidth;
        }
      } else {
        boxWidth = Math.min((Number(item.dataset.box) || 50) / 100 * stageWidth, stageHeight * 0.88);
        boxHeight = boxWidth;
      }
      item.style.aspectRatio = 'auto';
      item.style.width = `${boxWidth}px`;
      item.style.height = `${boxHeight}px`;
      item.style.transform = '';
      // Each bag sits dead centre of the stage; the scarf hangs from its handle.
      if (item === this.#bag) current = { boxWidth, boxHeight, top: (stageHeight - boxHeight) / 2 };
    }

    const overlay = this.querySelector('[data-closet-overlay]');
    if (!overlay || !current || !overlay.naturalWidth) return;
    const isCut = !overlay.classList.contains('mix-blend-multiply');
    // Tied on the handle, the scarf hangs in proportion to the bag: about 80% of the bag's height,
    // never longer than a real tied twilly (about a third of its length).
    const tied = Math.min(hang * 0.7, current.boxHeight * 0.8);
    const height = isCut ? tied : tied / 0.88;
    const width = height * (overlay.naturalWidth / overlay.naturalHeight);
    // Knotted at the bag's right handle; the scarf leans left, so its tails fall against the bag.
    const anchorX = stageWidth / 2 + current.boxWidth * 0.36;
    overlay.style.width = `${width}px`;
    overlay.style.height = `${height}px`;
    overlay.style.left = `${anchorX - width * 0.75}px`;
    overlay.style.top = `${current.top + current.boxHeight * 0.03}px`;
  }

  #cutouts = new Map();
  #cutoutChain = Promise.resolve();

  #queueCutout(img) {
    if (img.dataset.cutoutDone) return;
    img.dataset.cutoutDone = 'pending';
    this.#cutoutChain = this.#cutoutChain.then(async () => {
      const src = img.dataset.cutoutSrc || img.currentSrc || img.src;
      const url = await this.#cutoutUrl(src);
      // A colour change while this was processing has already replaced the photo.
      if (!url || (img.dataset.cutoutSrc || img.currentSrc || img.src) !== src) return;
      img.removeAttribute('srcset');
      img.removeAttribute('sizes');
      img.src = url;
      img.classList.remove('mix-blend-multiply');
      try { await img.decode(); } catch { /* layout falls back to the square box */ }
      img.dataset.cutoutDone = 'true';
      this.#place();
    });
  }

  // Removes a photo's plain studio background: flood-fills from the edges through pixels close
  // to the edge colour, fading alpha across a soft band. Returns null when it can't (e.g. CORS).
  #cutoutUrl(src, leanLeft = false) {
    if (!src) return Promise.resolve(null);
    const key = `${leanLeft ? 'scarf:' : ''}${src}`;
    if (!this.#cutouts.has(key)) this.#cutouts.set(key, this.#makeCutout(src, leanLeft).catch(() => null));
    return this.#cutouts.get(key);
  }

  async #makeCutout(src, leanLeft = false) {
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.src = src;
    await image.decode();
    const scale = Math.min(1, 900 / Math.max(image.naturalWidth, image.naturalHeight));
    const width = Math.max(1, Math.round(image.naturalWidth * scale));
    const height = Math.max(1, Math.round(image.naturalHeight * scale));
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d', { willReadFrequently: true });
    context.drawImage(image, 0, 0, width, height);
    const frame = context.getImageData(0, 0, width, height);
    const data = frame.data;

    const total = width * height;
    const ring = (inset) => {
      const points = [];
      const x0 = inset, y0 = inset, x1 = width - 1 - inset, y1 = height - 1 - inset;
      for (let x = x0; x <= x1; x += 3) points.push(y0 * width + x, y1 * width + x);
      for (let y = y0; y <= y1; y += 3) points.push(y * width + x0, y * width + x1);
      return points;
    };
    const median = (points) => {
      const opaque = points.filter((p) => data[p * 4 + 3] >= 200);
      if (opaque.length < points.length * 0.5) return null;
      const channel = (c) => {
        const values = opaque.map((p) => data[p * 4 + c]).sort((a, b) => a - b);
        return values[Math.floor(values.length / 2)];
      };
      return [channel(0), channel(1), channel(2)];
    };

    // Background colours: the outer edge, plus a ring just inside it for photos framed in a second tone.
    const outerRing = ring(0);
    const innerRing = ring(Math.round(Math.min(width, height) * 0.035));
    const colours = [median(outerRing), median(innerRing)].filter(Boolean);
    const distance = (p) => {
      let best = Infinity;
      for (const [r, g, b] of colours) {
        best = Math.min(best, Math.hypot(data[p * 4] - r, data[p * 4 + 1] - g, data[p * 4 + 2] - b));
      }
      return best;
    };

    // Photos that already have a see-through background skip the removal and are only cropped.
    if (colours.length) {
      const hard = 26;
      const soft = 52;
      const seen = new Uint8Array(total);
      const queue = new Int32Array(total);
      let head = 0, tail = 0;
      for (const p of [...outerRing, ...innerRing]) {
        if (!seen[p] && data[p * 4 + 3] >= 10 && distance(p) < soft) { seen[p] = 1; queue[tail++] = p; }
      }
      while (head < tail) {
        const p = queue[head++];
        const d = distance(p);
        const alpha = d <= hard ? 0 : Math.round(((d - hard) / (soft - hard)) * 255);
        data[p * 4 + 3] = Math.min(data[p * 4 + 3], alpha);
        if (d > hard) continue;
        const x = p % width;
        const neighbours = [p - width, p + width, x > 0 ? p - 1 : -1, x < width - 1 ? p + 1 : -1];
        for (const q of neighbours) {
          if (q < 0 || q >= total || seen[q]) continue;
          if (data[q * 4 + 3] < 10 || distance(q) < soft) { seen[q] = 1; queue[tail++] = q; }
        }
      }
      // Clear the outer frame band entirely so a two-tone border never survives.
      const band = Math.round(Math.min(width, height) * 0.02);
      for (let y = 0; y < height; y += 1) {
        for (let x = 0; x < width; x += 1) {
          if (x < band || y < band || x >= width - band || y >= height - band) {
            const p = y * width + x;
            if (distance(p) < 60) data[p * 4 + 3] = 0;
          }
        }
      }
    }
    context.putImageData(frame, 0, 0);
    const isBackground = (p) => colours.length > 0 && distance(p) < 60;

    // Crop to the product's own outline so its real size can be drawn exactly.
    let minX = width, minY = height, maxX = -1, maxY = -1;
    for (let y = 0; y < height; y += 1) {
      for (let x = 0; x < width; x += 1) {
        const p = y * width + x;
        // Ignore soft shadows and leftover background so the crop hugs the product itself.
        if (data[p * 4 + 3] < 40 || isBackground(p)) continue;
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }
    let output = canvas;
    if (maxX > minX && maxY > minY) {
      output = document.createElement('canvas');
      output.width = maxX - minX + 1;
      output.height = maxY - minY + 1;
      output.getContext('2d').drawImage(canvas, minX, minY, output.width, output.height, 0, 0, output.width, output.height);
    }
    // Scarves all hang the same way: one whose tails flare to the right is mirrored to lean left.
    if (leanLeft && maxX > minX && maxY > minY) {
      const rowCentre = (y0, y1) => {
        let sum = 0, count = 0;
        for (let y = y0; y <= y1; y += 1) {
          for (let x = minX; x <= maxX; x += 2) {
            if (data[(y * width + x) * 4 + 3] >= 40) { sum += x; count += 1; }
          }
        }
        return count ? sum / count : 0;
      };
      const band = Math.max(1, Math.round((maxY - minY) * 0.15));
      if (rowCentre(maxY - band, maxY) > rowCentre(minY, minY + band)) {
        const mirrored = document.createElement('canvas');
        mirrored.width = output.width;
        mirrored.height = output.height;
        const mirroredContext = mirrored.getContext('2d');
        mirroredContext.translate(mirrored.width, 0);
        mirroredContext.scale(-1, 1);
        mirroredContext.drawImage(output, 0, 0);
        output = mirrored;
      }
    }
    const blob = await new Promise((resolve) => output.toBlob(resolve, 'image/png'));
    return blob ? URL.createObjectURL(blob) : null;
  }

  #updateSummary() {
    const set = (selector, item) => {
      const link = this.querySelector(selector);
      if (!link || !item) return;
      link.textContent = item.dataset.label || '';
      link.href = item.dataset.url || '#';
    };
    set('[data-closet-bag-name]', this.#bag);
    set('[data-closet-scarf-name]', this.#scarf);
    const bagCurrent = this.querySelector('[data-closet-bag-current]');
    if (bagCurrent && this.#bag) bagCurrent.textContent = this.#bag.dataset.label || '';
    const scarfCurrent = this.querySelector('[data-closet-scarf-current]');
    if (scarfCurrent && this.#scarf) scarfCurrent.textContent = this.#scarf.dataset.label || '';

    const bagPrice = Number(this.#bag?.dataset.price || 0);
    const scarfPrice = Number(this.#scarf?.dataset.price || 0);
    const setPrice = (selector, cents) => {
      const el = this.querySelector(selector);
      if (el) el.textContent = this.#money(cents);
    };
    setPrice('[data-closet-bag-price]', bagPrice);
    setPrice('[data-closet-scarf-price]', scarfPrice);
    setPrice('[data-closet-total]', bagPrice + scarfPrice);

    const bagInput = this.querySelector('[data-closet-bag-input]');
    const scarfInput = this.querySelector('[data-closet-scarf-input]');
    if (bagInput && this.#bag) bagInput.value = this.#bag.dataset.variantId;
    if (scarfInput && this.#scarf) scarfInput.value = this.#scarf.dataset.variantId;
  }

  #money(cents) {
    const amount = cents / 100;
    return this.#formatter ? this.#formatter.format(amount) : amount.toFixed(2);
  }

  async #addLook(submitter) {
    if (!this.#bag || !this.#scarf || this.getAttribute('aria-busy') === 'true') return;
    const error = this.querySelector('[data-closet-error]');
    if (error) error.hidden = true;
    this.setAttribute('aria-busy', 'true');
    submitter?.setAttribute('aria-disabled', 'true');
    submitter?.classList.add('opacity-50', 'cursor-wait');

    try {
      const result = await window.Shopify.actions.updateCart(
        {
          lines: [
            { merchandiseId: this.#bag.dataset.variantId, quantity: 1 },
            { merchandiseId: this.#scarf.dataset.variantId, quantity: 1 },
          ],
        },
        { event: { context: 'product', detail: { source: 's-closet' } } }
      );
      const message = result?.userErrors?.[0]?.message;
      if (message) throw new Error(message);
      await window.Shopify.actions.openCart?.();
    } catch (err) {
      if (error) {
        error.textContent = err?.message || this.dataset.errorMessage || '';
        error.hidden = false;
      }
    } finally {
      this.removeAttribute('aria-busy');
      submitter?.removeAttribute('aria-disabled');
      submitter?.classList.remove('opacity-50', 'cursor-wait');
    }
  }
}

if (!customElements.get('s-closet')) {
  customElements.define('s-closet', SCloset);
}
